// eslint-disable-next-line jira/restricted/react-component-props
import React, { type ComponentProps } from 'react';
import flow from 'lodash/flow';
import memoizeOne from 'memoize-one';
import { type CreateUIAnalyticsEvent, withAnalyticsEvents } from '@atlaskit/analytics-next';
import AnalyticsData from '@atlassian/jira-analytics-web-react/src/components/analytics-data.tsx';
import { AnalyticsSource } from '@atlassian/jira-analytics-web-react/src/components/decorators.tsx';
import withFireUiAnalytics from '@atlassian/jira-analytics-web-react/src/components/with-fire-ui-analytics.tsx';
import { SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { connect } from '@atlassian/jira-react-redux/src/index.tsx';
import { ANALYTIC_CONTEXTS } from '@atlassian/jira-servicedesk-common/src/utils/analytics/index.tsx';
import type { OrderBy } from '@atlassian/jira-servicedesk-knowledge-services/src/services/rest/articles/index.tsx';
import type {
	Article,
	GenericError,
} from '@atlassian/jira-servicedesk-knowledge-services/src/types.tsx';
import KnowledgeHubPushProvider, {
	type PushHistory,
} from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/knowledge-hub-push-provider/index.tsx';
import { withRouter } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/knowledge-hub-with-router/index.tsx';
import { browseKnowledge } from '../../state/actions/browse-knowledge/index.tsx';
import { fetchAppLink } from '../../state/actions/fetch-applink/index.tsx';
import { fetchLinkedSpaces } from '../../state/actions/fetch-linked-spaces/index.tsx';
import { getAppLinkOrError } from '../../state/selectors/applink/index.tsx';
import { getArticlesByIds } from '../../state/selectors/articles/index.tsx';
import {
	getProjectId,
	getProjectKey,
	getIsAdmin,
	getActivationId,
} from '../../state/selectors/base/index.tsx';
import {
	getArticleIdsForCategory,
	getCategoryTotalSize,
	getBrowseLimit,
	getBrowseNextCursor,
	getBrowsePrevCursor,
	isBrowseLoading,
	isCategoriesSupported,
} from '../../state/selectors/browse/index.tsx';
import {
	getHubLinkedState,
	getLinkedConfluenceSpace,
	getLinkedConfluenceUrl,
} from '../../state/selectors/hub/index.tsx';
import { getLinkedSpaces } from '../../state/selectors/spaces/index.tsx';
import type { State } from '../../state/types.tsx';
import ReadyForUser from '../common/ready-for-user/index.tsx';
import SearchKnowledgeInline from '../common/search-knowledge-inline/index.tsx';
import AllArticlesTable from './all-articles-table/index.tsx';
import parseNumber from './utils/parse-number/index.tsx';
import KnowledgeHubBrowseAllArticles, {
	type KnowledgeHubBrowseArticlesProps,
	type Page,
} from './view.tsx';

type ConnectedProps = {
	page: string | undefined;
	cursor: string;
	sortBy: OrderBy | undefined;
	searchQuery: string;
	spaceKey: string;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	onCreateArticleEmptyStateClicked: KnowledgeHubBrowseArticlesProps['onCreateArticleEmptyStateClicked'];
	onCreateArticleHeaderClicked: KnowledgeHubBrowseArticlesProps['onCreateArticleHeaderClicked'];
};

type Props = JSX.LibraryManagedAttributes<
	typeof KnowledgeHubBrowseAllArticles,
	ComponentProps<typeof KnowledgeHubBrowseAllArticles>
>;

type ExternalProps = Flow.Diff<
	Props,
	{
		push: PushHistory;
	}
>;

const KnowledgeHubBrowseAllArticlesWithPush = (props: ExternalProps) => (
	<KnowledgeHubPushProvider>
		{({ push }) => <KnowledgeHubBrowseAllArticles {...props} push={push} />}
	</KnowledgeHubPushProvider>
);

const withRouterPush = withRouter(KnowledgeHubBrowseAllArticlesWithPush);

const mapStateToPropsFactory = () => {
	const spaceKeysFactory = memoizeOne((spaceKey: string) => (spaceKey ? JSON.parse(spaceKey) : {}));
	const articlesPageFactory = memoizeOne(
		(state: State, articleIds: string[] | GenericError | undefined) => ({
			totalSize: getCategoryTotalSize(state, 'all-articles'),
			// @ts-expect-error - TS2322 - Type 'Article | GenericError' is not assignable to type 'Article[]'. | TS2345 - Argument of type 'string[] | GenericError' is not assignable to parameter of type 'string[]'.
			articles: getArticlesByIds(state, articleIds).reduce(
				/* eslint-disable-next-line jira/js/no-reduce-accumulator-spread */ // @ts-expect-error - TS2769 - No overload matches this call. | TS2339 - Property 'error' does not exist on type 'Article | GenericError'.
				(acc, curr) => (curr.error ? acc : [...acc, curr]),
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				[] as Article[],
			),
		}),
	);
	return (state: State, ownProps: ConnectedProps) => {
		const page = parseNumber(ownProps.page, 1);
		const { cursor } = ownProps;
		const sortBy = ownProps.sortBy ?? '-lastModified';
		const searchQuery = ownProps.searchQuery ? ownProps.searchQuery : '';
		const spaceKeys = spaceKeysFactory(ownProps.spaceKey);
		const articleIds = getArticleIdsForCategory(state, 'all-articles', page);
		const activationId = getActivationId(state);
		let error: GenericError | undefined;
		let articlesPage: Page | undefined;

		if (!articleIds) {
			articlesPage = undefined;
			// @ts-expect-error - TS2339 - Property 'error' does not exist on type 'string[] | GenericError'.
		} else if (articleIds.error && !Array.isArray(articleIds)) {
			error = articleIds;
		} else {
			// @ts-expect-error - TS2322 - Type 'Article' is not assignable to type 'Article[]'.
			articlesPage = articlesPageFactory(state, articleIds);
		}

		return {
			appLinkOrError: getAppLinkOrError(state),
			linkedState: getHubLinkedState(state),
			spaceKey: getLinkedConfluenceSpace(state),
			confluenceUrl: getLinkedConfluenceUrl(state),
			limit: getBrowseLimit(state),
			isLoading: isBrowseLoading(state, 'all-articles'),
			categoriesSupported: isCategoriesSupported(state, 'all-articles'),
			page,
			sortBy,
			error,
			articlesPage,
			ReadyForUser,
			activationId,
			SearchKnowledgeInline,
			ArticlesTable: AllArticlesTable,
			projectId: getProjectId(state),
			projectKey: getProjectKey(state),
			linkedSpaces: getLinkedSpaces(state),
			cursor,
			searchQuery,
			spaceKeys,
			prevCursor: getBrowsePrevCursor(state, 'all-articles'),
			nextCursor: getBrowseNextCursor(state, 'all-articles'),
			isAdmin: getIsAdmin(state),
		};
	};
};

export default flow([
	AnalyticsSource('knowledgeBrowseAllArticles', SCREEN),
	AnalyticsData({
		attributes: {
			context: ANALYTIC_CONTEXTS.KNOWLEDGE,
		},
	}),
	withAnalyticsEvents(),
	withFireUiAnalytics({
		onCreateArticleHeaderClicked: 'createArticleHeaderLink',
		onCreateArticleEmptyStateClicked: 'createArticleEmptyStateLink',
	}),
	connect(mapStateToPropsFactory, {
		onBrowseArticles: browseKnowledge,
		getLinkedSpaces: fetchLinkedSpaces,
		onFetchAppLink: fetchAppLink,
	}),
])(withRouterPush);
